import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";

dayjs.extend(customParseFormat);

const THEME_COLOR = "#8b1634";
const SURFACE_TINT = "#f7ebf0";

const pickerTheme = createTheme({
  palette: {
    primary: {
      main: THEME_COLOR,
      dark: "#6d1128",
      light: "#a83252",
      contrastText: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 28,
        },
      },
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          color: THEME_COLOR,
          padding: "16px 24px 8px",
        },
        title: {
          color: THEME_COLOR,
          fontWeight: 500,
          fontSize: "0.875rem",
        },
      },
    },
    MuiPickersToolbarText: {
      styleOverrides: {
        root: {
          color: THEME_COLOR,
        },
        selected: {
          color: THEME_COLOR,
        },
      },
    },
    MuiTimeClock: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
        },
      },
    },
    MuiClockNumber: {
      styleOverrides: {
        root: {
          color: THEME_COLOR,
        },
        selected: {
          color: "#ffffff",
        },
      },
    },
    MuiClockPointer: {
      styleOverrides: {
        root: {
          backgroundColor: THEME_COLOR,
        },
        thumb: {
          backgroundColor: THEME_COLOR,
          borderColor: THEME_COLOR,
        },
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        item: {
          borderRadius: 8,
          color: THEME_COLOR,
          "&.Mui-selected": {
            backgroundColor: SURFACE_TINT,
            color: THEME_COLOR,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "8px 16px 16px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        textPrimary: {
          color: THEME_COLOR,
          fontWeight: 700,
        },
      },
    },
  },
});

function parseTimeValue(value) {
  if (value == null || String(value).trim() === "") return null;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d/.test(raw)) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return dayjs().hour(d.getHours()).minute(d.getMinutes()).second(0).millisecond(0);
    }
  }
  const formats = ["HH:mm:ss", "HH:mm", "H:mm", "h:mm A", "hh:mm A"];
  for (const format of formats) {
    const parsed = dayjs(raw, format, true);
    if (parsed.isValid()) {
      return dayjs().hour(parsed.hour()).minute(parsed.minute()).second(0).millisecond(0);
    }
  }
  return null;
}

function formatTimeValue(dayjsValue) {
  if (!dayjsValue || !dayjsValue.isValid()) return "";
  return dayjsValue.format("HH:mm");
}

function formatDisplayValue(value) {
  const parsed = parseTimeValue(value);
  if (!parsed) return "";
  return parsed.format("HH:mm");
}

function HiddenTextField() {
  return null;
}

function AnalogClockTimePicker({
  id,
  value,
  onChange,
  disabled = false,
  isClearable = true,
  placeholder = "Select time",
  "aria-label": ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(null);
  const displayValue = useMemo(() => formatDisplayValue(value), [value]);

  useEffect(() => {
    if (open) {
      setDraftValue(parseTimeValue(value));
    }
  }, [open, value]);

  const handleAccept = (acceptedValue) => {
    onChange?.(formatTimeValue(acceptedValue));
    setOpen(false);
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onChange?.("");
  };

  return (
    <ThemeProvider theme={pickerTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className="fatigue-analog-time-picker fatigue-time-picker-wrap">
          <input
            id={id}
            type="text"
            readOnly
            value={displayValue}
            placeholder={placeholder}
            disabled={disabled}
            aria-label={ariaLabel}
            className="fatigue-theme-time-input"
            onClick={() => {
              if (!disabled) setOpen(true);
            }}
            onKeyDown={(event) => {
              if (disabled) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setOpen(true);
              }
            }}
          />
          {isClearable && displayValue && !disabled ? (
            <button
              type="button"
              className="fatigue-analog-time-clear"
              onClick={handleClear}
              aria-label="Clear time"
            >
              ×
            </button>
          ) : null}
          <MobileTimePicker
            ampm
            open={open}
            value={draftValue}
            views={["hours", "minutes"]}
            minutesStep={5}
            onClose={() => setOpen(false)}
            onChange={(nextValue) => setDraftValue(nextValue)}
            onAccept={handleAccept}
            slots={{
              textField: HiddenTextField,
            }}
            slotProps={{
              mobilePaper: {
                className: "fatigue-mui-time-dialog",
                elevation: 8,
              },
              toolbar: {
                hidden: false,
              },
              actionBar: {
                actions: ["cancel", "accept"],
              },
            }}
            localeText={{
              toolbarTitle: "Select time",
              cancelButtonLabel: "Cancel",
              okButtonLabel: "OK",
            }}
            desktopModeMediaQuery="@media (min-width: 0px)"
          />
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default AnalogClockTimePicker;
